import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import type TextRenderer from "bpmn-js/lib/draw/TextRenderer";
import LabelEditingProvider from "bpmn-js/lib/features/label-editing/LabelEditingProvider";
import type BpmnFactory from "bpmn-js/lib/features/modeling/BpmnFactory";
import type Modeling from "bpmn-js/lib/features/modeling/Modeling";
import { Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import type Canvas from "diagram-js/lib/core/Canvas";
import type EventBus from "diagram-js/lib/core/EventBus";
import type DirectEditing from "diagram-js/lib/features/direct-editing/DirectEditing";
import type ResizeHandles from "diagram-js/lib/features/resize/ResizeHandles";
import { ShapeLike } from "diagram-js/lib/model/Types";
import { App, createApp, markRaw } from "vue";

import ReferenceSelector from "@/components/BPMN/ReferenceSelector.vue";
import {
    elementIsCallActivity,
    elementIsCollapsedPool,
} from "@/composables/bpmn-manager/helpers";
import { pinia } from "@/pinia";
import { initPrimeVue } from "@/primevue/init";
import router from "@/router";
import FontAwesomeButton from "@/shared/FontAwesomeButton.vue";
import { EntityType } from "@/stores/entity-store";

const MARKER_HIDDEN = "djs-element-hidden";
const MARKER_LABEL_HIDDEN = "djs-label-hidden";

export default class CustomLabelEditingProvider extends LabelEditingProvider {
    _app: App | null = null;
    _el: HTMLElement | null = null;
    // Incremented on every initVueComponent() call so deferred callbacks
    // (see below) can detect that a newer editing session has since started
    // and skip acting on stale state instead of tearing down that session.
    _sessionId = 0;
    _modeling: Modeling;
    _canvas: Canvas;
    _eventBus: EventBus;
    _directEditing: DirectEditing;
    _viewboxChangedListener: (() => void) | null = null;

    constructor(
        eventBus: EventBus,
        bpmnFactory: BpmnFactory,
        canvas: Canvas,
        directEditing: DirectEditing,
        modeling: Modeling,
        resizeHandles: ResizeHandles,
        textRenderer: TextRenderer
    ) {
        super(
            eventBus,
            bpmnFactory,
            canvas,
            directEditing,
            modeling,
            resizeHandles,
            textRenderer
        );
        this._canvas = canvas;
        this._modeling = modeling;
        this._eventBus = eventBus;
        this._directEditing = directEditing;

        // Listen for deactivation to clean up our Vue component
        eventBus.on("directEditing.deactivate", () => {
            // Use setTimeout to ensure we don't unmount while an event is still processing
            // (e.g. PrimeVue trying to focus back after hide)
            const sessionId = this._sessionId;
            setTimeout(() => {
                // A newer session may have already started (and synchronously
                // cleaned up/replaced this._app/this._el) since this callback
                // was scheduled. Only clean up if we're still that session,
                // otherwise we'd tear down the newer one instead.
                if (this._sessionId === sessionId) {
                    this.cleanup();
                }
            }, 0);
        });

        // Ensure label stays visible even if LabelEditingPreview tries to hide it.
        // We use a lower priority (500) to run after LabelEditingPreview (1000).
        eventBus.on(
            "directEditing.activate",
            500,
            (context: { active: { element: ShapeLike } }) => {
                const element = context.active.element;
                if (
                    elementIsCallActivity(element) ||
                    elementIsCollapsedPool(element)
                ) {
                    // DirectEditing creates a hidden dummy textbox for every
                    // activation and ties real DOM focus to it (via the text
                    // selection it sets). It also auto-completes editing the
                    // moment that textbox loses focus to anything outside it —
                    // which fires as soon as our ReferenceSelector's TreeSelect
                    // opens and focuses itself, tearing the overlay right back
                    // down. We own completion entirely through the TreeSelect's
                    // own close/hide, so drop DirectEditing's own focusout
                    // listener on the dummy textbox for this session.
                    const textbox = (
                        directEditing as unknown as {
                            _textbox?: {
                                content?: HTMLElement;
                                focusoutHandler?: EventListener;
                            };
                        }
                    )._textbox;
                    if (textbox?.content && textbox?.focusoutHandler) {
                        textbox.content.removeEventListener(
                            "focusout",
                            textbox.focusoutHandler
                        );
                    }
                    // Use setTimeout to ensure we run after the next tick where markers might be added
                    setTimeout(() => {
                        canvas.removeMarker(
                            element.label || element,
                            MARKER_HIDDEN
                        );
                        canvas.removeMarker(
                            element.label || element,
                            MARKER_LABEL_HIDDEN
                        );
                    });
                }
            }
        );
    }

    activate(element: BPMNElement) {
        if (elementIsCallActivity(element) || elementIsCollapsedPool(element)) {
            this.initVueComponent(element);

            const bo = getBusinessObject(element);

            // Return a dummy context so directEditing considers itself active.
            // We use display: 'none' to ensure the hidden default textbox
            // doesn't intercept any mouse events or hide the text.
            return {
                bounds: { x: 0, y: 0, width: 0, height: 0 },
                text: bo.name || "",
                style: {
                    display: "none",
                },
            };
        }

        // Fallback to default behavior for all other elements
        return super.activate(element);
    }

    update(
        element: BPMNElement,
        newLabel: string,
        activeContextText: string,
        bounds: unknown
    ) {
        if (elementIsCallActivity(element) || elementIsCollapsedPool(element)) {
            // We handle updates in the Vue component (ReferenceSelector.vue),
            // so we ignore the default update call from DirectEditing.
            return;
        }
        super.update(element, newLabel, activeContextText, bounds);
    }

    updateElBounds(element: BPMNElement) {
        if (!this._el) return;
        const viewbox = this._canvas.viewbox();
        this._el.style.left = `${(element.x - viewbox.x) * viewbox.scale}px`;
        this._el.style.top = `${(element.y - viewbox.y) * viewbox.scale}px`;
        this._el.style.width = `${element.width * viewbox.scale}px`;
        this._el.style.height = `${element.height * viewbox.scale}px`;
        this._el.style.setProperty("--canvas-scale", String(viewbox.scale));
    }

    initVueComponent(element: BPMNElement) {
        this.cleanup(); // Ensure any existing instance is removed
        this._sessionId += 1;
        const sessionId = this._sessionId;

        const container = this._canvas.getContainer();
        const el = document.createElement("div");
        this._el = el;

        el.style.position = "absolute";
        el.style.zIndex = "100";
        // Enable pointer events so the TreeSelect can be clicked
        el.style.pointerEvents = "auto";
        this.updateElBounds(element);

        // Keep the overlay in sync when the user zooms or pans
        this._viewboxChangedListener = () => this.updateElBounds(element);
        this._eventBus.on(
            "canvas.viewbox.changed",
            this._viewboxChangedListener
        );

        container.appendChild(el);

        const isCallActivity = elementIsCallActivity(element);

        this._app = createApp(ReferenceSelector, {
            element: markRaw(element),
            modeling: markRaw(this._modeling),
            eventBus: markRaw(this._eventBus),
            allowedEntityTypes: isCallActivity
                ? [EntityType.PROCESS]
                : [EntityType.DOMAIN, EntityType.PRODUCT, EntityType.PROCESS],
            onClose: () => {
                // Ignore stale close callbacks from a session that's already
                // been superseded by a newer one (see _sessionId above).
                if (this._sessionId === sessionId) {
                    this._directEditing.complete();
                }
            },
            allowCustomValue: !isCallActivity,
        });

        initPrimeVue(this._app);
        this._app.use(router);
        // Share the main app's Pinia: a fresh instance would re-create every
        // store the selector touches, refiring their initial fetches
        // (get_worktree & co.) on each open even though the main app's
        // stores are already in sync.
        this._app.use(pinia);
        this._app.component("FontAwesomeIcon", FontAwesomeIcon);
        this._app.component("FontAwesomeButton", FontAwesomeButton);

        this._app.mount(el);
    }

    cleanup() {
        if (this._viewboxChangedListener) {
            this._eventBus.off(
                "canvas.viewbox.changed",
                this._viewboxChangedListener
            );
            this._viewboxChangedListener = null;
        }
        if (this._app) {
            this._app.unmount();
            this._app = null;
        }
        if (this._el) {
            this._el.remove();
            this._el = null;
        }
    }
}

CustomLabelEditingProvider.$inject = [
    "eventBus",
    "bpmnFactory",
    "canvas",
    "directEditing",
    "modeling",
    "resizeHandles",
    "textRenderer",
];
